import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Radio,
  Button,
  Space,
  message,
  Alert,
  Tag,
} from 'antd';
import { SaveOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { permissionsAPI } from '../services/api.ts';
import type { PermissionValue } from '../contexts/AuthContext.tsx';

const { Title, Paragraph, Text } = Typography;

interface ResourceRow {
  key: string;
  label: string;
  group: string;
  read_only: boolean;
}

interface LevelInfo {
  id: number;
  level_name: string;
}

type MatrixState = Record<number, Record<string, PermissionValue>>;

const Permissions: React.FC = () => {
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [levels, setLevels] = useState<LevelInfo[]>([]);
  const [matrix, setMatrix] = useState<MatrixState>({});
  const [original, setOriginal] = useState<MatrixState>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadMatrix = async () => {
    setLoading(true);
    try {
      const data = await permissionsAPI.getMatrix();
      setResources(data.resources || []);
      setLevels(data.levels || []);
      const perms: MatrixState = {};
      (data.levels || []).forEach((l: LevelInfo) => {
        perms[l.id] = { ...(data.permissions?.[l.id] || {}) };
      });
      setMatrix(perms);
      setOriginal(JSON.parse(JSON.stringify(perms)));
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel caricamento dei permessi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMatrix();
  }, []);

  const hasChanges = useMemo(
    () => JSON.stringify(matrix) !== JSON.stringify(original),
    [matrix, original]
  );

  const handleChange = (levelId: number, resource: string, value: PermissionValue) => {
    setMatrix((prev) => ({
      ...prev,
      [levelId]: { ...prev[levelId], [resource]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const entries: { level_id: number; resource: string; permission: string }[] = [];
      levels.forEach((l) => {
        resources.forEach((r) => {
          const value = matrix[l.id]?.[r.key];
          if (value && value !== original[l.id]?.[r.key]) {
            entries.push({ level_id: l.id, resource: r.key, permission: value });
          }
        });
      });
      if (entries.length === 0) {
        message.info('Nessuna modifica da salvare');
        return;
      }
      await permissionsAPI.updateMatrix(entries);
      message.success(`Permessi aggiornati (${entries.length} modifiche). Gli utenti li vedranno al prossimo caricamento pagina.`);
      setOriginal(JSON.parse(JSON.stringify(matrix)));
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel salvataggio dei permessi');
    } finally {
      setSaving(false);
    }
  };

  // rowSpan per raggruppare visivamente le voci per gruppo di menu
  const groupRowSpan = (record: ResourceRow, index: number) => {
    const prev = resources[index - 1];
    if (prev && prev.group === record.group) return 0;
    let span = 1;
    for (let i = index + 1; i < resources.length; i++) {
      if (resources[i].group === record.group) span++;
      else break;
    }
    return span;
  };

  const columns = [
    {
      title: 'Sezione',
      dataIndex: 'group',
      key: 'group',
      width: 140,
      onCell: (record: ResourceRow, index?: number) => ({
        rowSpan: groupRowSpan(record, index ?? 0),
      }),
      render: (group: string) => <Text strong>{group}</Text>,
    },
    {
      title: 'Voce di menu',
      dataIndex: 'label',
      key: 'label',
      render: (label: string, record: ResourceRow) => (
        <Space>
          <span className="no-uppercase-modal">{label}</span>
          {record.read_only && <Tag color="blue">solo lettura</Tag>}
        </Space>
      ),
    },
    ...levels.map((level) => ({
      title: level.level_name,
      key: `level-${level.id}`,
      width: 320,
      render: (_: any, record: ResourceRow) => {
        const value: PermissionValue = matrix[level.id]?.[record.key] ?? 'write';
        const changed = value !== (original[level.id]?.[record.key] ?? 'write');
        return (
          <Radio.Group
            size="small"
            buttonStyle="solid"
            value={value}
            onChange={(e) => handleChange(level.id, record.key, e.target.value)}
            style={changed ? { outline: '2px solid #faad14', borderRadius: 6 } : undefined}
          >
            <Radio.Button value="none">Nessuno</Radio.Button>
            <Radio.Button value="read">Lettura</Radio.Button>
            {!record.read_only && <Radio.Button value="write">Scrittura</Radio.Button>}
          </Radio.Group>
        );
      },
    })),
  ];

  return (
    <div>
      <Title level={3}>
        <SafetyOutlined /> Permessi
      </Title>
      <Paragraph type="secondary">
        Per ogni <strong>livello utente</strong> imposta l'accesso a ciascuna voce di menu:
        <Tag style={{ marginLeft: 6 }}>Nessuno</Tag> la voce sparisce dal menu e le API rifiutano le richieste;
        <Tag>Lettura</Tag> la pagina è consultabile ma i pulsanti di scrittura sono nascosti e le API rifiutano le modifiche;
        <Tag>Scrittura</Tag> accesso completo. Il livello <strong>AMMINISTRATORE</strong> ha sempre accesso completo e
        non è configurabile; le aree Strumenti e Impostazioni (API, Permessi, Utenti) restano riservate agli amministratori.
        Le modifiche hanno effetto sulle API immediatamente e a video al prossimo caricamento pagina degli utenti.
      </Paragraph>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Le anagrafiche usate come elenchi a tendina (clienti, fornitori, prodotti, magazzini, ecc.) restano sempre leggibili dalle altre pagine anche con permesso 'Nessuno': il blocco vale per la pagina dedicata e per le operazioni di scrittura."
      />
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges}
          >
            Salva permessi
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadMatrix} loading={loading}>
            Ricarica
          </Button>
          {hasChanges && <Text type="warning">Modifiche non salvate</Text>}
        </Space>
        <Table<ResourceRow>
          rowKey="key"
          columns={columns}
          dataSource={resources}
          loading={loading}
          pagination={false}
          size="small"
          bordered
        />
      </Card>
    </div>
  );
};

export default Permissions;
